/**
 * Verkäufe & Abgleich (Verwaltung): Tagesliste, nicht zugeordnete SumUp-Zahlungen (manuell zuordnen /
 * als Ladenware buchen), Storno & Erstattungen (SumUp-Erstattungen mit Rückgaben verknüpfen).
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, PageHeader } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag, formatUhrzeit, formatDatumZeit, tagVon } from "@shared/zeit";
import { useAlsLadenware, useErstattungVerknuepfen, useVerkaeufe, useVerkaufAbbrechen, useZuordnen, type OffeneZahlung, type VerkaeufeAntwort, type VerkaufZeile } from "./verkaeufe-api";

const STATUS: Record<string, { badge: "wait" | "paid" | "ambiguous" | "notfound" | "neutral" | "attention"; text: string }> = {
  offen: { badge: "neutral", text: "Offen" },
  wartet_auf_zahlung: { badge: "wait", text: "Wartet auf Zahlung" },
  bezahlt: { badge: "paid", text: "Bezahlt" },
  mehrdeutig: { badge: "ambiguous", text: "Mehrdeutig" },
  nicht_gefunden: { badge: "notfound", text: "Nicht gefunden" },
  abgebrochen: { badge: "neutral", text: "Abgebrochen" },
};

function zahlungsartText(z: string | null) {
  return z === "bar" ? "Bar" : z === "karte" ? "Karte" : z === "sonstig" ? "Sonstig" : "–";
}

function fehlerText(err: unknown, standard: string) {
  return err instanceof ApiFehler ? err.message : standard;
}

function tagPlus(tag: string, tage: number) {
  const d = new Date(`${tag}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
}

export function Verkaeufe() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "alle";
  const tag = params.get("tag") ?? tagVon();
  const daten = useVerkaeufe(tag);
  const setzen = (p: Record<string, string>) => setParams({ tab, tag, ...p });

  return (
    <>
      <PageHeader
        kicker="Heute"
        title="Verkäufe und Abgleich"
        sub="Jeder Warenkorb, jede SumUp-Zahlung – und was noch nicht zusammenpasst"
        actions={
          <Link to="/laden/verkaeufe/csv-import" className="rg-btn rg-btn--secondary">
            CSV-Notfall-Import
          </Link>
        }
      />
      <nav className="app-tabs" aria-label="Ansicht">
        {(
          [
            ["alle", "Alle"],
            ["offen", `Nicht zugeordnet${daten.data?.nichtZugeordnet.length ? ` (${daten.data.nichtZugeordnet.length})` : ""}`],
            ["erstattungen", "Storno & Erstattungen"],
          ] as [string, string][]
        ).map(([k, t]) => (
          <button key={k} type="button" className="sc-pill" aria-pressed={tab === k} onClick={() => setzen({ tab: k })}>
            {t}
          </button>
        ))}
      </nav>
      <Seitenzustand query={daten} lines={6}>
        {(d) => (tab === "offen" ? <NichtZugeordnet daten={d} /> : tab === "erstattungen" ? <Erstattungen daten={d} /> : <Tagesliste daten={d} tag={tag} setTag={(t) => setzen({ tag: t })} />)}
      </Seitenzustand>
    </>
  );
}

// --- Tab „Alle“ ----------------------------------------------------------------------------------

function Tagesliste({ daten, tag, setTag }: { daten: VerkaeufeAntwort; tag: string; setTag: (t: string) => void }) {
  const abbrechen = useVerkaufAbbrechen();
  const [fehler, setFehler] = useState<string | null>(null);
  const summe = daten.verkaeufe.filter((v) => v.status === "bezahlt").reduce((a, v) => a + v.summeCent, 0);
  const heute = tagVon();
  const offen = daten.verkaeufe.filter((v) => ["wartet_auf_zahlung", "mehrdeutig", "nicht_gefunden"].includes(v.status));

  return (
    <Card
      title={
        <span className="app-tag-nav">
          <Button variant="quiet" icon="chevron-left" aria-label="Vortag" onClick={() => setTag(tagPlus(tag, -1))} />
          <span>{tag === heute ? "Heute" : formatTag(tag)}</span>
          <Button variant="quiet" icon="chevron-right" aria-label="Folgetag" disabled={tag >= heute} onClick={() => setTag(tagPlus(tag, 1))} />
        </span>
      }
      actions={
        <span className="sc-muted">
          {daten.verkaeufe.filter((v) => v.status === "bezahlt").length} bezahlt · {formatEuro(summe)}
          {offen.length ? ` · ${offen.length} offen` : ""}
        </span>
      }
      flush
    >
      {fehler ? <div style={{ padding: "var(--space-3) var(--space-4) 0" }}><Notice tone="error">{fehler}</Notice></div> : null}
      {daten.verkaeufe.length === 0 ? (
        <EmptyState compact illustration="empty-shelf" title="Keine Verkäufe an diesem Tag">
          Sobald an der Kasse ein Warenkorb abgeschlossen wird, erscheint er hier.
        </EmptyState>
      ) : (
        <div className="app-tabellen-scroll">
          <table className="rg-table">
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Zeit</th>
                <th>Artikel</th>
                <th>Summe</th>
                <th>Zahlung</th>
                <th>Status</th>
                <th>Kasse</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {daten.verkaeufe.map((v) => (
                <VerkaufRow
                  key={v.id}
                  v={v}
                  onAbbrechen={() => {
                    if (!confirm(`Warenkorb #${v.nummer} abbrechen? Die Artikel gehen zurück ins Regal.`)) return;
                    setFehler(null);
                    abbrechen.mutate({ id: v.id, grund: "Abbruch in der Verwaltung" }, { onError: (e) => setFehler(fehlerText(e, "Abbrechen hat nicht geklappt.")) });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function VerkaufRow({ v, onAbbrechen }: { v: VerkaufZeile; onAbbrechen: () => void }) {
  const st = STATUS[v.status] ?? { badge: "neutral" as const, text: v.status };
  const offen = ["wartet_auf_zahlung", "mehrdeutig", "nicht_gefunden"].includes(v.status);
  return (
    <tr className={offen ? "app-zeile--offen" : undefined}>
      <td className="app-code">#{v.nummer}</td>
      <td>{formatUhrzeit(v.bezahltAt ?? v.abgeschlossenAt ?? v.erstelltAt)}</td>
      <td>
        {v.positionen.map((p, i) => (
          <div key={i}>
            {p.bezeichnung}
            <span className="sc-muted sc-small">
              {" "}
              · {p.mieterName ?? "Laden"}
              {p.rabattCent ? ` · Rabatt −${formatEuro(p.rabattCent)}` : ""}
            </span>
          </div>
        ))}
      </td>
      <td className="sc-strong">{formatEuro(v.summeCent)}</td>
      <td>
        {zahlungsartText(v.zahlungsart)}
        {v.transactionCode ? <div className="app-code sc-muted">{v.transactionCode}</div> : null}
        {v.zuordnung === "manuell" ? <div className="sc-muted sc-small">manuell zugeordnet</div> : null}
      </td>
      <td>
        <StatusBadge status={st.badge}>{st.text}</StatusBadge>
      </td>
      <td className="sc-muted">{[v.geraetName, v.mitarbeiterName].filter(Boolean).join(" · ") || "–"}</td>
      <td>
        {offen ? (
          <Button variant="quiet" icon="trash-2" onClick={onAbbrechen}>
            Abbrechen
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

// --- Tab „Nicht zugeordnet“ ----------------------------------------------------------------------

function NichtZugeordnet({ daten }: { daten: VerkaeufeAntwort }) {
  const zuordnen = useZuordnen();
  const ladenware = useAlsLadenware();
  const [fehler, setFehler] = useState<string | null>(null);
  const [lw, setLw] = useState<OffeneZahlung | null>(null);
  const [bezeichnung, setBezeichnung] = useState("Ladenware");

  if (daten.nichtZugeordnet.length === 0) {
    return (
      <Card>
        <EmptyState compact illustration="all-done" title="Alles zugeordnet">
          Jede SumUp-Zahlung der letzten 14 Tage gehört zu einem Warenkorb.
        </EmptyState>
      </Card>
    );
  }

  return (
    <>
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <Notice tone="info">
        Diese Zahlungen hat SumUp gemeldet, aber kein Warenkorb passt eindeutig. Zuordnen, wenn ein Kandidat passt – oder als Ladenware buchen, wenn es kein Mieterartikel war. Der Mieter bekommt sein Geld erst mit der Zuordnung gutgeschrieben.
      </Notice>
      <div className="app-form">
        {daten.nichtZugeordnet.map((z) => (
          <Card
            key={z.id}
            title={
              <span className="sc-row">
                <span className="sc-strong">{formatEuro(z.betragCent)}</span>
                <span className="sc-muted">{zahlungsartText(z.zahlungsart)}{z.kartendetails ? ` · ${z.kartendetails}` : ""} · {formatDatumZeit(z.zeitpunkt)}</span>
              </span>
            }
            actions={<span className="app-code sc-muted">{z.transactionCode}{z.quelle === "csv" ? " · CSV" : ""}</span>}
          >
            {z.kandidaten.length === 0 ? (
              <p className="sc-muted">Kein Warenkorb mit diesem Betrag in ±30 Minuten.</p>
            ) : (
              <div className="sc-col">
                {z.kandidaten.map((k) => (
                  <div key={k.id} className="sc-row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
                    <span>
                      <span className="sc-strong">#{k.nummer}</span> · {formatEuro(k.summeCent)} · {k.abgeschlossenAt ? formatUhrzeit(k.abgeschlossenAt) : "–"} · <StatusBadge status={STATUS[k.status]?.badge ?? "neutral"}>{STATUS[k.status]?.text ?? k.status}</StatusBadge>
                      <span className="sc-muted sc-small"> · {k.positionen}</span>
                    </span>
                    <Button variant="primary" icon="check" loading={zuordnen.isPending} onClick={() => { setFehler(null); zuordnen.mutate({ transactionCode: z.transactionCode, warenkorbId: k.id }, { onError: (e) => setFehler(fehlerText(e, "Zuordnen hat nicht geklappt.")) }); }}>
                      Zuordnen
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }}>
              <Button variant="secondary" icon="store" onClick={() => { setLw(z); setBezeichnung("Ladenware"); }}>
                Als Ladenware buchen
              </Button>
            </div>
          </Card>
        ))}
      </div>
      <Dialog
        open={!!lw}
        title="Als Ladenware buchen"
        size="sm"
        onClose={() => setLw(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setLw(null)}>Abbrechen</Button>
            <Button variant="primary" icon="store" loading={ladenware.isPending} onClick={() => lw && ladenware.mutate({ transactionCode: lw.transactionCode, bezeichnung }, { onSuccess: () => setLw(null), onError: (e) => setFehler(fehlerText(e, "Buchen hat nicht geklappt.")) })}>
              Buchen
            </Button>
          </>
        }
      >
        {lw ? (
          <div className="app-form">
            <p>
              {formatEuro(lw.betragCent)} vom {formatDatumZeit(lw.zeitpunkt)} wird dem Laden gutgeschrieben – kein Mieter ist beteiligt.
            </p>
            <TextField label="Bezeichnung" value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} maxLength={80} autoFocus onFocus={(e) => e.target.select()} />
          </div>
        ) : null}
      </Dialog>
    </>
  );
}

// --- Tab „Storno & Erstattungen“ -----------------------------------------------------------------

function Erstattungen({ daten }: { daten: VerkaeufeAntwort }) {
  const verknuepfen = useErstattungVerknuepfen();
  const [fehler, setFehler] = useState<string | null>(null);
  const { transaktionen, offeneRueckgaben } = daten.erstattungen;
  const unverknuepft = transaktionen.filter((t) => !t.rueckgabeId);

  return (
    <>
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      {offeneRueckgaben.length ? (
        <Card title={`Rückgaben, deren SumUp-Erstattung noch fehlt (${offeneRueckgaben.length})`}>
          <p className="sc-muted">An der Kasse wurde „in SumUp erstattet“ bestätigt. Sobald SumUp die Erstattung meldet, hier verknüpfen.</p>
          <div className="sc-col">
            {offeneRueckgaben.map((r) => (
              <div key={r.id} className="sc-row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
                <span>
                  <span className="sc-strong">{r.bezeichnung}</span> <span className="app-code sc-muted">{r.code}</span> · {formatEuro(r.betragCent)} · {formatDatumZeit(r.erstelltAt)} · {r.grund}
                </span>
                {unverknuepft.length ? (
                  <span className="sc-toolbar">
                    {unverknuepft
                      .filter((t) => Math.abs(t.betragCent) === r.betragCent)
                      .map((t) => (
                        <Button key={t.id} variant="secondary" icon="check" loading={verknuepfen.isPending} onClick={() => { setFehler(null); verknuepfen.mutate({ transactionCode: t.transactionCode, rueckgabeId: r.id }, { onError: (e) => setFehler(fehlerText(e, "Verknüpfen hat nicht geklappt.")) }); }}>
                          {t.transactionCode} · {formatDatumZeit(t.zeitpunkt)}
                        </Button>
                      ))}
                  </span>
                ) : (
                  <span className="sc-muted sc-small">Noch keine passende Erstattung von SumUp</span>
                )}
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      <Card title="Erstattungen und Rückbuchungen (30 Tage)" flush>
        {transaktionen.length === 0 ? (
          <EmptyState compact illustration="all-done" title="Keine Erstattungen">
            Erstattungen aus SumUp erscheinen hier, sobald sie gemeldet werden.
          </EmptyState>
        ) : (
          <div className="app-tabellen-scroll">
            <table className="rg-table">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Betrag</th>
                  <th>Art</th>
                  <th>Status</th>
                  <th>Warenkorb</th>
                  <th>Rückgabe</th>
                </tr>
              </thead>
              <tbody>
                {transaktionen.map((t) => (
                  <tr key={t.id} className={t.rueckgabeId ? undefined : "app-zeile--offen"}>
                    <td>{formatDatumZeit(t.zeitpunkt)}</td>
                    <td className="sc-strong">{formatEuro(t.betragCent)}</td>
                    <td>{t.typ === "rueckbuchung" ? "Rückbuchung" : "Erstattung"} · {zahlungsartText(t.zahlungsart)}</td>
                    <td className="app-code">{t.status}</td>
                    <td>{t.warenkorbNummer ? `#${t.warenkorbNummer}` : "–"}</td>
                    <td>{t.rueckgabeId ? <StatusBadge status="paid">Verknüpft</StatusBadge> : <StatusBadge status="attention">Offen</StatusBadge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
